/**
 * Call Stats Analytics API
 *
 * Handles call statistics and series funnel analytics endpoints.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  callStatsResponseSchema,
  missedCallsResponseSchema,
  seriesFunnelResponseSchema,
  outcomeFunnelResponseSchema,
} from '@aica/api-types/schemas';
import type {
  CallStatsResponse,
  MissedCallsResponse,
  SeriesFunnelResponse,
  OutcomeFunnelResponse,
} from '@aica/api-types/schemas';

export class CallStatsApi extends BaseApiClient {
  /**
   * Get aggregated call statistics for a period.
   * Data is role-scoped on the backend.
   */
  async getCallStats(params: {
    period: string;
    periodStart?: string;
    periodEnd?: string;
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
    includeDemo?: 'yes' | 'no';
    /** `records.type` — narrows every part of the answer. Omitted: calls and chats. */
    channel?: 'call' | 'chat';
  }): Promise<CallStatsResponse> {
    const qs = new URLSearchParams({ period: params.period });
    if (params.periodStart) qs.set('periodStart', params.periodStart);
    if (params.periodEnd) qs.set('periodEnd', params.periodEnd);
    if (params.projectIds && params.projectIds.length > 0) {
      qs.set('projectIds', params.projectIds.join(','));
    } else if (params.projectId) {
      qs.set('projectId', params.projectId);
    }
    if (params.includeDemo) qs.set('includeDemo', params.includeDemo);
    if (params.channel) qs.set('channel', params.channel);
    // Minutes east of UTC (WS1-T6) — buckets byHour/dailyTrend in the user's
    // local timezone instead of UTC.
    qs.set('tzOffset', String(-new Date().getTimezoneOffset()));
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_CALL_STATS}?${qs}`,
      callStatsResponseSchema
    );
  }

  /**
   * «Пропущенные»: the flow's missed-call definitions counted on the server —
   * calls only, the unknown direction apart — and the table by employee.
   * Scoped like the records list (`records.read`).
   */
  async getMissedCalls(params: {
    period: string;
    periodStart?: string;
    periodEnd?: string;
    /** Empty/omitted = every project the viewer may see. */
    projectIds?: string[];
    includeDemo?: 'yes' | 'no';
  }): Promise<MissedCallsResponse> {
    const qs = new URLSearchParams({ period: params.period });
    if (params.periodStart) qs.set('periodStart', params.periodStart);
    if (params.periodEnd) qs.set('periodEnd', params.periodEnd);
    if (params.projectIds && params.projectIds.length > 0) {
      qs.set('projectIds', params.projectIds.join(','));
    }
    if (params.includeDemo) qs.set('includeDemo', params.includeDemo);
    const summary = await this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_MISSED_CALLS}?${qs}`,
      missedCallsResponseSchema
    );
    // An edge that does not know `projectIds` ignores it and answers for the
    // whole organization: under a project filter those are other numbers than
    // the ones asked for, so it is a failure, not an answer.
    if (
      params.projectIds &&
      params.projectIds.length > 0 &&
      summary.projectIds === undefined
    ) {
      throw new Error('Missed calls: the server did not apply the project filter');
    }
    return summary;
  }

  /**
   * Get call series funnel data.
   * Shows how many series reach each step number.
   */
  async getSeriesFunnel(params: {
    period: string;
    periodStart?: string;
    periodEnd?: string;
    entityType?: 'deal' | 'lead';
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
    includeDemo?: 'yes' | 'no';
  }): Promise<SeriesFunnelResponse> {
    const qs = new URLSearchParams({ period: params.period });
    if (params.includeDemo) qs.set('includeDemo', params.includeDemo);
    if (params.periodStart) qs.set('periodStart', params.periodStart);
    if (params.periodEnd) qs.set('periodEnd', params.periodEnd);
    if (params.entityType) qs.set('entityType', params.entityType);
    if (params.projectIds && params.projectIds.length > 0) {
      qs.set('projectIds', params.projectIds.join(','));
    } else if (params.projectId) {
      qs.set('projectId', params.projectId);
    }
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_SERIES_FUNNEL}?${qs}`,
      seriesFunnelResponseSchema
    );
  }

  /**
   * Get the "quality x conversion" outcome funnel (Phase C, C-5).
   * Series started -> Deal open -> Won / Lost, plus avg quality by outcome
   * and a stage x quality breakdown.
   */
  async getOutcomeFunnel(params: {
    period: string;
    periodStart?: string;
    periodEnd?: string;
    /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
    projectId?: string;
    /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
    projectIds?: string[];
    includeDemo?: 'yes' | 'no';
  }): Promise<OutcomeFunnelResponse> {
    const qs = new URLSearchParams({ period: params.period });
    if (params.includeDemo) qs.set('includeDemo', params.includeDemo);
    if (params.periodStart) qs.set('periodStart', params.periodStart);
    if (params.periodEnd) qs.set('periodEnd', params.periodEnd);
    if (params.projectIds && params.projectIds.length > 0) {
      qs.set('projectIds', params.projectIds.join(','));
    } else if (params.projectId) {
      qs.set('projectId', params.projectId);
    }
    return this.validatedRequest(
      `${API_ENDPOINTS.ANALYTICS_OUTCOME_FUNNEL}?${qs}`,
      outcomeFunnelResponseSchema
    );
  }
}
